/**
 * RowMeta — the Case Portal's at-a-glance orientation fields as one compact
 * line: referred-by (OF) · case type · jurisdiction · received
 * (P-LIST-022/023/024/025). Ported onto CC's active-book cards (Under Review book,
 * Accepted book) in the portal→CC merge (ADR-0067, slice 2b) so a card carries the
 * same identity the portal's table columns do, while the cards keep CC's native
 * time-bars / outreach reads.
 *
 * Shared because both active books render it identically. Parts with no datum
 * are dropped; an all-empty line renders nothing. Position alone disambiguates
 * (referred-by leads, as in the portal); only the date is labelled, since a
 * bare date is ambiguous.
 */
import { cn } from "@/lib/utils";
import { dayZone } from "@/lib/format";

/** Received date — month + day. The day comes from `dayZone`: a date-only
 *  Creation Date is its own day, an instant its Pacific day. */
function fmtReceived(iso: string): string {
  const z = dayZone(iso);
  if (z === null) return "";
  return new Date(z.ms).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: z.timeZone,
  });
}

export function RowMeta({
  originatingFirmName,
  caseType,
  incidentState,
  received,
  className,
  testId = "row-meta",
}: {
  originatingFirmName: string | null;
  caseType: string | null;
  incidentState: string | null;
  /** ISO date/datetime; date-only is parsed as local (see fmtReceived). */
  received: string | null;
  className?: string;
  testId?: string;
}) {
  const bits: string[] = [];
  if (originatingFirmName) bits.push(originatingFirmName);
  if (caseType) bits.push(caseType);
  if (incidentState) bits.push(incidentState);
  if (received) bits.push(`rec'd ${fmtReceived(received)}`);
  if (bits.length === 0) return null;

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground",
        className,
      )}
      data-testid={testId}
    >
      {bits.map((b, i) => (
        <span key={i} className="flex items-center gap-x-1.5">
          {i > 0 ? (
            <span aria-hidden className="opacity-50">
              ·
            </span>
          ) : null}
          <span>{b}</span>
        </span>
      ))}
    </div>
  );
}
